import React, { useEffect, useState } from "react";
import { Trophy, UserRound, Crown, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { TV } from "./TriviaTheme";
import { useVisitor } from "../context/VisitorAuthContext";
import { Link } from "react-router-dom";
import { ChampionCardButton } from "./ChampionCard";

const YouChampion = ({ d, boards }) => {
  const { visitor } = useVisitor();
  const mine = boards.filter((b) => visitor?.nickname && d.champions?.[b] === visitor.nickname);
  if (!mine.length) return null;
  return (
    <div className="mt-4 rounded-[14px] bg-[#F6EBCB] border border-[#D9B65C] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="you-are-champion">
      <p className="font-ui text-sm font-semibold text-[#4A3708] flex items-center gap-1.5"><Crown className="w-4 h-4" /> You were last week's Weekly Champion. Post your win!</p>
      <div className="flex flex-wrap gap-2">
        {mine.map((b) => {
          const t = d.last_week[b];
          return <ChampionCardButton key={b} board={b} nickname={visitor.nickname} week={d.last_week.week} points={t.top[0].points} accuracy={t.top[0].accuracy} players={t.players}
            label={b === "sport" ? "Get your card" : `${b.toUpperCase()} card`} />;
        })}
      </div>
    </div>
  );
};

const HallLink = () => (
  <Link to="/trivia/champions" data-testid="hall-of-champions-link" className="inline-flex items-center gap-1 font-ui text-xs font-semibold text-[#2D4A3E] hover:underline">
    <Crown className="w-3.5 h-3.5" /> Hall of Champions
  </Link>
);

export const useIsChampion = (board) => {
  const { visitor } = useVisitor();
  const [champs, setChamps] = useState(null);
  useEffect(() => { api.get("/trivia/chart-leaderboard").then((r) => setChamps(r.data.champions)).catch(() => {}); }, []);
  return !!(visitor?.nickname && champs?.[board] && champs[board] === visitor.nickname);
};

const TopTen = ({ rows, side, champion }) => (
  <ol className="mt-3 divide-y divide-[#ECE6DB] border border-[#ECE6DB] rounded-[14px] bg-white overflow-hidden" data-testid={`top10-${side}`}>
    {rows.length ? rows.map((r, i) => (
      <li key={r.nickname} data-testid={`top10-${side}-row-${i}`} className="flex items-center justify-between gap-3 px-4 py-2 font-ui text-sm">
        <span className="flex items-center gap-3 min-w-0">
          <span className={`w-6 text-center font-bold tabular-nums ${i < 3 ? "text-[#2D4A3E]" : "text-[#A9A399]"}`}>{i + 1}</span>
          <span className="font-semibold text-[#161514] truncate">{r.nickname}</span>
          {champion && r.nickname === champion && (
            <span data-testid={`champion-crown-${side}`} title="Last week's Weekly Champion" className="inline-flex items-center gap-1 rounded-full bg-[#F6EBCB] border border-[#D9B65C] px-2 py-0.5 text-[10px] font-bold text-[#7A5A12] shrink-0">
              <Crown className="w-3 h-3" /> Champion
            </span>
          )}
        </span>
        <span className="text-[#77716A] tabular-nums shrink-0"><b className="text-[#161514]">{r.points}</b> pts · {r.plays}d</span>
      </li>
    )) : <li className="px-4 py-3 font-ui text-xs text-[#77716A]">No named players yet this week. Be the first.</li>}
  </ol>
);

const NicknamePrompt = () => {
  const { visitor, openAuth, refresh } = useVisitor();
  const [nick, setNick] = useState("");
  const [busy, setBusy] = useState(false);
  if (visitor === null) return null;
  if (!visitor) {
    return (
      <button data-testid="leaderboard-signin" onClick={openAuth} className="mt-3 inline-flex items-center gap-1.5 font-ui text-xs font-semibold text-[#2D4A3E] hover:underline">
        <UserRound className="w-3.5 h-3.5" /> Sign in and pick a nickname to get on the top 10
      </button>
    );
  }
  if (visitor.nickname) {
    const toggleRecap = async () => {
      await api.put("/visitor/recap", { enabled: !!visitor.recap_opt_out });
      await refresh();
      toast.success(visitor.recap_opt_out ? "Monday recap email turned on" : "Monday recap email turned off");
    };
    return (
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 font-ui text-xs text-[#77716A]">
        <p data-testid="leaderboard-you">Playing as <b className="text-[#161514]">{visitor.nickname}</b></p>
        <button data-testid="recap-toggle" onClick={toggleRecap} className="inline-flex items-center gap-1.5 font-semibold text-[#2D4A3E] hover:underline">
          <Mail className="w-3.5 h-3.5" /> Monday recap email: {visitor.recap_opt_out ? "Off · turn on" : "On · turn off"}
        </button>
      </div>
    );
  }
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/visitor/nickname", { nickname: nick });
      await refresh();
      toast.success("Nickname saved. Your Chart Threes now count for the top 10");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that nickname");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mt-3 flex gap-2" data-testid="nickname-form">
      <input data-testid="nickname-input" value={nick} onChange={(e) => setNick(e.target.value)} maxLength={20} placeholder="Pick a leaderboard nickname"
        className="flex-1 min-w-0 rounded-full border border-[#DDD5C7] bg-[#F7F4EE] px-4 py-2 font-ui text-sm text-[#161514] focus:outline-none focus:border-[#2D4A3E]" />
      <button data-testid="nickname-save" disabled={busy || nick.trim().length < 2} className={`${TV.pillDark} px-4 py-2 font-ui text-sm font-semibold disabled:opacity-40`}>Save</button>
    </form>
  );
};

const Side = ({ tag, name, t, lead, testid }) => (
  <div className={`flex-1 rounded-[16px] p-4 border ${lead ? "bg-[#E5EEE8] border-[#C7D9CE]" : "bg-[#F7F4EE] border-[#E4DED2]"}`} data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <p className="font-ui font-bold text-sm text-[#161514]">{tag} <span className="font-medium text-[#77716A]">{name}</span></p>
      {lead && <Trophy className="w-4 h-4 text-[#2D4A3E]" />}
    </div>
    <p className="font-ui font-bold text-3xl text-[#161514] tabular-nums mt-2" data-testid={`${testid}-accuracy`}>{t.accuracy}%</p>
    <p className="font-ui text-xs text-[#77716A] mt-1" data-testid={`${testid}-plays`}>
      {t.plays.toLocaleString()} {t.plays === 1 ? "play" : "plays"} · {t.perfect} perfect
    </p>
  </div>
);

export const ChartLeaderboard = ({ refresh = 0, region = "uk" }) => {
  const [d, setD] = useState(null);
  const [side, setSide] = useState(region);
  useEffect(() => { setSide(region); }, [region]);
  useEffect(() => { api.get("/trivia/chart-leaderboard").then((r) => setD(r.data)).catch(() => {}); }, [refresh]);
  if (!d) return null;
  const w = d.this_week, l = d.last_week;
  const ukLead = w.uk.accuracy > w.us.accuracy && w.uk.plays > 0;
  const usLead = w.us.accuracy > w.uk.accuracy && w.us.plays > 0;
  const gap = Math.abs(w.uk.accuracy - w.us.accuracy);
  const since = new Date(w.week + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  return (
    <div className={`${TV.card} p-5 tv-rise`} data-testid="chart-leaderboard">
      <div className="flex items-center justify-between gap-3">
        <p className="tv-caps text-[#57524B]">UK vs US · Chart Three this week</p>
        <span className="font-ui text-[11px] text-[#77716A]">since Mon {since}</span>
      </div>
      <p className="font-ui font-bold text-base sm:text-lg text-[#161514] mt-2" data-testid="chart-leaderboard-headline">
        {!w.uk.plays && !w.us.plays ? "No scores yet this week. Play to put your side on the board."
          : ukLead ? `The UK leads by ${gap} points` : usLead ? `The US leads by ${gap} points` : "Dead level, all to play for"}
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <Side tag="UK" name="Chart Three" t={w.uk} lead={ukLead} testid="leaderboard-uk" />
        <Side tag="US" name="Chart Three" t={w.us} lead={usLead} testid="leaderboard-us" />
      </div>
      <div className="mt-5 flex items-center justify-between gap-3">
        <p className="tv-caps !text-[11px] text-[#77716A]">Weekly top 10</p>
        <div className="inline-flex p-0.5 rounded-full bg-[#EEE8DD] border border-[#DDD5C7]" data-testid="top10-toggle">
          {["uk", "us"].map((r) => (
            <button key={r} data-testid={`top10-toggle-${r}`} onClick={() => setSide(r)}
              className={`px-3 py-1 rounded-full font-ui text-xs font-semibold transition-[background-color,color] ${side === r ? "bg-[#161514] text-white" : "text-[#4A453E]"}`}>{r.toUpperCase()}</button>
          ))}
        </div>
      </div>
      <YouChampion d={d} boards={["uk", "us"]} />
      <TopTen rows={w[side].top || []} side={side} champion={d.champions?.[side]} />
      <NicknamePrompt />
      {l.leader && (
        <p className="font-ui text-xs text-[#77716A] mt-3" data-testid="leaderboard-last-week">
          Last week: {l.leader.toUpperCase()} won, {l[l.leader].accuracy}% vs {l[l.leader === "uk" ? "us" : "uk"].accuracy}%
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
        <p className="font-ui text-[11px] text-[#A9A399]">Accuracy across every player's first Chart Three of the day. Resets each Monday.</p>
        <HallLink />
      </div>
    </div>
  );
};

export const SportLeaderboard = ({ refresh = 0 }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/trivia/chart-leaderboard").then((r) => setD(r.data)).catch(() => {}); }, [refresh]);
  if (!d) return null;
  const t = d.this_week.sport;
  return (
    <div className={`${TV.card} p-5 tv-rise`} data-testid="sport-leaderboard">
      <div className="flex items-center justify-between gap-3">
        <p className="tv-caps text-[#57524B]">Sport Three · this week</p>
        <span className="font-ui text-[11px] text-[#77716A]" data-testid="sport-leaderboard-stats">{t.plays} plays · {t.accuracy}% accuracy</span>
      </div>
      <p className="tv-caps !text-[11px] text-[#77716A] mt-4">Weekly top 10</p>
      <YouChampion d={d} boards={["sport"]} />
      <TopTen rows={t.top || []} side="sport" champion={d.champions?.sport} />
      <NicknamePrompt />
      <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
        {d.champions?.sport ? <p className="font-ui text-xs text-[#77716A]" data-testid="sport-last-champion">Last week's champion: <b className="text-[#161514]">{d.champions.sport}</b></p> : <span />}
        <HallLink />
      </div>
    </div>
  );
};
